'use client'

import type { ChangeEvent, FC } from 'react'
import { useCallback, useRef, useState } from 'react'
import { useLocalisation } from '@/tools/i18n/client'
import { useErrorHandling } from '@/tools/o11y/client'
import { EnvelopeIcon, TextField } from '@/tools/ui'

export interface EmailInputChangeEvent {
  isEmailValid: boolean
  email: string
}

interface Props {
  onChange: (event: EmailInputChangeEvent) => void
  errorMessage?: string
  initialValue?: string
  showIcon?: boolean
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
}

export const EmailInput: FC<Props> = ({
  onChange,
  errorMessage,
  initialValue,
  showIcon = true,
  onKeyDown: handleKeyDown,
}) => {
  const { t } = useLocalisation()
  const { withErrorTracking } = useErrorHandling()

  const [email, setEmail] = useState<string>(initialValue || '')
  const [isEmailValid, setIsEmailValid] = useState<boolean>(false)
  const [hasTyped, setHasTyped] = useState<boolean>(false)
  const emailRef = useRef<HTMLInputElement | null>(null)
  const placeholder = initialValue || t('account.email')

  const getErrorMessage = () => {
    if (errorMessage) {
      return errorMessage
    }
    if (!email && hasTyped) {
      return t('signup.error.email-required')
    } else if (!isEmailValid && hasTyped && email) {
      return t('auth.invalid-email')
    }
    return ''
  }

  const computedErrorMessage = getErrorMessage()

  const checkValidity = (email: string) => {
    if (!email) {
      return false
    }
    return (emailRef.current && emailRef.current.checkValidity()) || false
  }
  const handleEmailChange = withErrorTracking(
    useCallback(
      (value: string) => {
        setEmail(value)
        const isEmailValid = checkValidity(value)
        setIsEmailValid(isEmailValid)
        onChange({ email: value, isEmailValid })
      },
      [onChange]
    )
  )
  const handleOnBlur = withErrorTracking(() => {
    setHasTyped(true)
  })
  return (
    <TextField
      ref={emailRef}
      aria-label={t('account.email')}
      error={computedErrorMessage}
      label={t('account.email')}
      leadingIcon={showIcon ? EnvelopeIcon : undefined}
      placeholder={placeholder}
      type="email"
      value={email}
      onBlur={handleOnBlur}
      onChange={(e: ChangeEvent<HTMLInputElement>) => handleEmailChange(e.target.value)}
      onKeyDown={handleKeyDown}
    />
  )
}
